import { isValidPhoneNumber, parsePhoneNumber, formatPhoneNumber } from 'react-phone-number-input';

// Country phone prefixes
export const countryPhonePrefixes = {
    BE: '+32',
    FR: '+33',
    NL: '+31',
    DE: '+49',
    LU: '+352',
    GB: '+44',
    ES: '+34',
    IT: '+39',
    PT: '+351',
    CH: '+41',
    AT: '+43',
    US: '+1',
    CA: '+1',
};

/**
 * Normalizes a phone number to E.164 format
 * @param {string} phone - Phone number to normalize
 * @param {string} defaultCountry - Default country code (default: 'BE')
 * @returns {string|null} Normalized phone number or null if invalid
 */
export const normalizePhoneNumber = (phone, defaultCountry = 'BE') => {
    if (!phone || typeof phone !== 'string') return null;

    // Remove common formatting characters
    const cleaned = phone.replace(/[\s\-\(\)\/\.]/g, '');

    // If already in E.164 format, return as is
    if (cleaned.startsWith('+')) {
        try {
            const parsed = parsePhoneNumber(cleaned);
            return parsed ? parsed.number : null;
        } catch (e) {
            return null;
        }
    }

    // Get the prefix for the country
    const prefix = countryPhonePrefixes[defaultCountry] || '+32';

    // Try to parse with default country
    try {
        const withCountry = cleaned.startsWith('0')
            ? `${prefix}${cleaned.substring(1)}` // Number starting with 0
            : `+${cleaned}`; // Already has country code without +

        const parsed = parsePhoneNumber(withCountry);
        return parsed ? parsed.number : null;
    } catch (e) {
        return null;
    }
};

/**
 * Formats a phone number for display based on country
 * @param {string} phone - Phone number in E.164 format
 * @param {string} country - Country code
 * @returns {string} Formatted phone number
 */
export const formatPhoneForDisplay = (phone, country = 'BE') => {
    if (!phone) return '';
    try {
        // If not in E.164 format, try to normalize first
        const normalized = phone.startsWith('+') ? phone : normalizePhoneNumber(phone, country);
        if (!normalized) return phone;

        const parsed = parsePhoneNumber(normalized);
        if (parsed) {
            return formatPhoneNumber(normalized) || phone;
        }
        return phone;
    } catch (e) {
        return phone;
    }
};

/**
 * Validates an email address format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email) => {
    if (!email) return true; // Email is optional unless marked required
    // Check for standard email format AND valid TLD (at least 2 letters)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

/**
 * Validates a phone number using react-phone-number-input
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export const isValidPhone = (phone) => {
    if (!phone) return true; // Phone is optional
    // Clean the phone number
    const cleaned = phone.replace(/[\s\-\(\)\/\.]/g, '');
    if (cleaned.length < 9) return false;

    // Try to validate with library
    try {
        return isValidPhoneNumber(phone);
    } catch (e) {
        // Fallback to basic validation
        return /^\+?[\d\s\-\(\)]{9,}$/.test(phone);
    }
};

/**
 * Validates a website URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
export const isValidUrl = (url) => {
    if (!url) return true; // Website is optional
    try {
        // Check if URL starts with http:// or https://
        // AND has a valid TLD (at least 2 letters at the end)
        const urlRegex = /^https?:\/\/.+\.[a-z]{2,}/i;
        if (!urlRegex.test(url)) {
            return false;
        }
        // Try to create a URL object to validate structure
        new URL(url);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Ensures URL has protocol prefix
 * @param {string} url - URL to check
 * @param {string} protocol - Protocol to add (default: 'https://')
 * @returns {string} URL with protocol
 */
export const ensureUrlProtocol = (url, protocol = 'https://') => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    return `${protocol}${url}`;
};

/**
 * Validates a social media URL
 * @param {string} url - URL to validate
 * @param {string} platform - Platform name (facebook, twitter, youtube, instagram, linkedin)
 * @returns {boolean} True if valid
 */
export const isValidSocialUrl = (url, platform) => {
    if (!url) return true; // Social URLs are optional

    const patterns = {
        facebook: /^https?:\/\/(www\.)?facebook\.com\/.+/i,
        twitter: /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+/i,
        youtube: /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/i,
        instagram: /^https?:\/\/(www\.)?instagram\.com\/.+/i,
        linkedin: /^https?:\/\/(www\.)?linkedin\.com\/.+/i,
    };

    const pattern = patterns[platform];
    if (!pattern) return isValidUrl(url);

    return pattern.test(url);
};

/**
 * Validates a Belgian VAT number
 * @param {string} vat - VAT number to validate
 * @returns {boolean} True if valid
 */
export const isValidBelgianVAT = (vat) => {
    if (!vat) return true; // VAT is optional
    // Belgian VAT format: BE 0XXX.XXX.XXX or BE0XXXXXXXXX
    const cleaned = vat.replace(/[\s\.\-]/g, '').toUpperCase();
    return /^BE[01]\d{9}$/.test(cleaned);
};

/**
 * Validates a French VAT number
 * @param {string} vat - VAT number to validate
 * @returns {boolean} True if valid
 */
export const isValidFrenchVAT = (vat) => {
    if (!vat) return true;
    const cleaned = vat.replace(/[\s\.\-]/g, '').toUpperCase();
    return /^FR[A-Z0-9]{2}\d{9}$/.test(cleaned);
};

/**
 * Validates a VAT number based on country
 * @param {string} vat - VAT number to validate
 * @param {string} country - Country code
 * @returns {boolean} True if valid
 */
export const isValidVAT = (vat, country) => {
    if (!vat) return true;

    switch (country) {
        case 'BE':
            return isValidBelgianVAT(vat);
        case 'FR':
            return isValidFrenchVAT(vat);
        default:
            // Basic validation for other countries
            return vat.length >= 8;
    }
};

/**
 * Validates a postal code based on country
 * @param {string} postalCode - Postal code to validate
 * @param {string} country - Country code
 * @returns {boolean} True if valid
 */
export const isValidPostalCode = (postalCode, country) => {
    if (!postalCode) return true;

    const patterns = {
        BE: /^\d{4}$/,           // Belgium: 4 digits
        FR: /^\d{5}$/,           // France: 5 digits
        NL: /^\d{4}\s?[A-Z]{2}$/i, // Netherlands: 4 digits + 2 letters
        DE: /^\d{5}$/,           // Germany: 5 digits
        LU: /^\d{4}$/,           // Luxembourg: 4 digits
        GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, // UK
        US: /^\d{5}(-\d{4})?$/,  // USA: 5 or 9 digits
    };

    const pattern = patterns[country];
    if (!pattern) return true; // Accept any format for unknown countries

    return pattern.test(postalCode.trim());
};

/**
 * Checks if a value is not empty
 * @param {string} value - Value to check
 * @returns {boolean} True if not empty
 */
export const isRequired = (value) => {
    if (value === null || value === undefined) return false;
    return String(value).trim().length > 0;
};

/**
 * Checks minimum length
 * @param {string} value - Value to check
 * @param {number} min - Minimum length
 * @returns {boolean} True if valid
 */
export const minLength = (value, min) => {
    if (!value) return false;
    return value.trim().length >= min;
};

/**
 * Checks maximum length
 * @param {string} value - Value to check
 * @param {number} max - Maximum length
 * @returns {boolean} True if valid
 */
export const maxLength = (value, max) => {
    if (!value) return true;
    return value.trim().length <= max;
};

// Error messages in French
export const errorMessages = {
    required: 'Ce champ est obligatoire',
    email: 'Adresse email invalide',
    phone: 'Numéro de téléphone invalide',
    url: "URL invalide (doit commencer par http:// ou https://)",
    socialUrl: (platform) => `URL ${platform} invalide`,
    minLength: (min) => `Doit contenir au moins ${min} caractères`,
    maxLength: (max) => `Ne peut pas dépasser ${max} caractères`,
    vat: 'Numéro de TVA invalide',
    postalCode: 'Code postal invalide',
};

/**
 * Validates Places/Endroits form data
 * @param {Object} formData - Form data to validate
 * @param {string} country - Country code for country-specific validation
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const validatePlaceForm = (formData, country = 'BE') => {
    const errors = {};

    // Required fields
    if (!isRequired(formData.placeName)) {
        errors.placeName = errorMessages.required;
    }

    if (!isRequired(formData.placeTypeId) && !isRequired(formData.placeType)) {
        errors.placeType = errorMessages.required;
    }

    // Optional fields with format validation
    if (formData.email && !isValidEmail(formData.email)) {
        errors.email = errorMessages.email;
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
        errors.phone = errorMessages.phone;
    }

    if (formData.website && !isValidUrl(formData.website)) {
        errors.website = errorMessages.url;
    }

    if (formData.tvaNum && !isValidVAT(formData.tvaNum, country)) {
        errors.tvaNum = errorMessages.vat;
    }

    if (formData.facebookUrl && !isValidSocialUrl(formData.facebookUrl, 'facebook')) {
        errors.facebookUrl = errorMessages.socialUrl('Facebook');
    }

    if (formData.twitterUrl && !isValidSocialUrl(formData.twitterUrl, 'twitter')) {
        errors.twitterUrl = errorMessages.socialUrl('Twitter/X');
    }

    if (formData.youtubeUrl && !isValidSocialUrl(formData.youtubeUrl, 'youtube')) {
        errors.youtubeUrl = errorMessages.socialUrl('YouTube');
    }

    return errors;
};

/**
 * Validates form data and returns errors (generic)
 * @param {Object} formData - Form data to validate
 * @param {Object} translations - Translation object for error messages
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const validateForm = (formData, translations) => {
    const errors = {};

    if (!isRequired(formData.firstName)) {
        errors.firstName = translations?.validation?.required || errorMessages.required;
    }

    if (!isRequired(formData.lastName)) {
        errors.lastName = translations?.validation?.required || errorMessages.required;
    }

    if (!isRequired(formData.email)) {
        errors.email = translations?.validation?.required || errorMessages.required;
    } else if (!isValidEmail(formData.email)) {
        errors.email = translations?.validation?.email || errorMessages.email;
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
        errors.phone = translations?.validation?.phone || errorMessages.phone;
    }

    if (formData.website && !isValidUrl(formData.website)) {
        errors.website = translations?.validation?.website || errorMessages.url;
    }

    return errors;
};

/**
 * Check if there are any errors
 * @param {Object} errors - Errors object
 * @returns {boolean} True if there are errors
 */
export const hasErrors = (errors) => {
    return Object.keys(errors).length > 0;
};
